import { CheckCircleTwoTone, HeartTwoTone, QuestionCircleTwoTone } from '@ant-design/icons'
import { Card, Checkbox, Collapse, Layout, List, Progress, Space, Tag, Typography } from 'antd'
import { Header, Content } from 'antd/es/layout/layout'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { HeaderMenu } from '../../components/header-menu'
import { emotionalIntelligenceChecklistItems as checklistItems } from './adulting-101-checklists'

const { Title, Paragraph } = Typography

const faqItems = [
  {
    question: 'Why should I take time to recognize what I’m feeling instead of just moving on?',
    answer: (
      <>
        Naming your emotion ("I'm anxious," "I'm disappointed") helps you understand why you're reacting a certain way instead of just
        feeling overwhelmed by it. This same kind of self-reflection is central to{' '}
        <Link to="/adulting-101/spiritual-awareness">Spiritual Awareness</Link>.
      </>
    ),
  },
  {
    question: "How do I stop getting so angry when things don't go my way?",
    answer: (
      <>
        Recognize anger early, take deep breaths, and count to ten before you respond. Once you calm down, you can think more clearly. This
        pause is exactly the kind of self-control that helps with <Link to="/adulting-101/problem-solving">Problem Solving</Link> — you
        can't think through a solution while you're still reacting.
      </>
    ),
  },
  {
    question: "How do I show empathy when I don't understand why someone is upset?",
    answer: (
      <>
        You don't need to fully understand their problem to show empathy — just listen without judging or trying to fix it. Saying "that
        sounds really hard" goes a long way. This is one of the most important skills covered in{' '}
        <Link to="/adulting-101/friends-and-family">Friends and Family</Link>.
      </>
    ),
  },
  {
    question: 'Why does noticing body language and tone of voice matter?',
    answer: (
      <>
        People often say they're "fine" when their tone or posture says otherwise. Picking up on these cues helps you respond to how someone
        actually feels, not just their words. It's the same observation skill used in{' '}
        <Link to="/adulting-101/communication-skills">Communication Skills</Link>.
      </>
    ),
  },
  {
    question: 'How do I express my feelings without shutting down or blowing up?',
    answer: (
      <>
        Use calm, direct language like "I felt hurt when..." instead of yelling or going silent. Practicing this in low-stakes moments makes
        it easier when emotions run high, similar to how you'd communicate clearly in{' '}
        <Link to="/adulting-101/like-love-lust">Like, Love, Lust</Link> relationships.
      </>
    ),
  },
  {
    question: 'How do I listen to criticism without getting defensive?',
    answer: (
      <>
        Take a breath before reacting, and try to hear the specific point being made instead of treating it as a personal attack. Ask
        yourself if there's truth in it you can learn from. Being open to feedback is part of showing{' '}
        <Link to="/adulting-101/responsibility">Responsibility</Link> — it means you care more about improving than about being right.
      </>
    ),
  },
  {
    question: 'How do I comfort someone who is struggling without making it worse?',
    answer: (
      <>
        Sit with them, listen, and avoid jumping straight to advice unless they ask for it. Sometimes just being present is enough. This
        kind of support is one of the foundations of <Link to="/adulting-101/friends-and-family">Friends and Family</Link>.
      </>
    ),
  },
  {
    question: "Why does respecting other people's boundaries matter so much?",
    answer: (
      <>
        Everyone needs space sometimes, even people who care about you. Pushing past someone's boundaries, even with good intentions, can
        damage trust. This matters a lot in <Link to="/adulting-101/like-love-lust">Like, Love, Lust</Link>, where respecting someone's "no"
        is essential.
      </>
    ),
  },
  {
    question: "How do I know if I'm emotionally intelligent?",
    answer: (
      <>
        You're working on it if you can name what you're feeling, don't blow up when things go wrong, and notice how your mood affects other
        people. This self-awareness carries over into being <Link to="/adulting-101/responsibility">Responsible</Link> for how your actions
        impact others.
      </>
    ),
  },
]

export function Adulting101EmotionalIntelligencePage() {
  const [completedIds, setCompletedIds] = useState<Set<string>>(() => new Set())
  const completedCount = completedIds.size
  const completionPercent = Math.round((completedCount / checklistItems.length) * 100)

  return (
    <Layout>
      <Header>
        <h1>Emotional Intelligence</h1>
      </Header>
      <HeaderMenu selected="adulting-101" />
      <Content style={{ padding: '20px', textAlign: 'left', background: 'linear-gradient(180deg, #f9f0ff 0%, #ffffff 220px)' }}>
        <Title level={2}>Emotional intelligence is understanding and managing your emotions, and recognizing emotions in others.</Title>
        <Paragraph type="secondary">
          People who can recognize their own feelings, manage them wisely, and understand how others feel tend to be happier, more
          successful, and better at relationships. It's a skill you can develop.
        </Paragraph>

        <Card
          title={
            <Space>
              <HeartTwoTone twoToneColor="#eb2f96" />
              Emotional Intelligence Checklist
            </Space>
          }
          style={{ marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
          extra={<Progress type="circle" percent={completionPercent} size={56} />}
        >
          <List
            dataSource={checklistItems}
            renderItem={(item) => (
              <List.Item>
                <Checkbox
                  checked={completedIds.has(item.id)}
                  onChange={(event) => {
                    setCompletedIds((currentIds) => {
                      const nextIds = new Set(currentIds)
                      if (event.target.checked) {
                        nextIds.add(item.id)
                      } else {
                        nextIds.delete(item.id)
                      }
                      return nextIds
                    })
                  }}
                >
                  {item.text}
                </Checkbox>
              </List.Item>
            )}
          />
          {completionPercent === 100 ? (
            <Tag color="success" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} style={{ marginTop: '12px' }}>
              Congratulations, you're being emotionally intelligent!
            </Tag>
          ) : (
            <Paragraph style={{ marginTop: '12px' }}>
              Emotional intelligence isn't about never feeling bad emotions. It's about understanding them and responding thoughtfully.
            </Paragraph>
          )}
        </Card>

        <Card
          title={
            <Space>
              <QuestionCircleTwoTone />
              FAQ: Emotional Intelligence
            </Space>
          }
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        >
          <Collapse
            items={faqItems.map((faq, index) => ({
              key: index,
              label: faq.question,
              children: <Paragraph>{faq.answer}</Paragraph>,
            }))}
          />
        </Card>
      </Content>
    </Layout>
  )
}
